import type { JobCard } from "~/interface/Job"
import { JobAction } from "~/interface/Job"

const LOCAL_STORAGE_PREFIX = "voiditem-dynamodb"

const stripT = (alias?: string) => (alias || "").replace(/^T/i, "")

const buildPayloads = (job: JobCard, customerId: string): any[] => {
  const items: any[] = Array.isArray((job as any).__items) ? (job as any).__items : []
  if (items.length === 0) return []

  const json2: Record<string, number> = (job as any).json2 || {}
  const reason: string | null = (job as any).__reason || null
  const employeeName = job.employee?.name || "unknown"
  const aliasNumeric = stripT(job.alias)
  const isPrint = (job as any).__noPrint ? false : true

  return items.map((it: any, idx: number) => {
    const negQty = Number(json2[it.uuid] || 0)
    const qty = Math.abs(negQty || it.quantity || 1)
    const price = Number(it.price ?? 0)
    const baseTs = job.timestamp ? new Date(job.timestamp as any).getTime() : Date.now()
    const timestamp = new Date(baseTs + idx).toISOString()
    return {
      customerId,
      timestamp,
      approvedBy: employeeName,
      cashier: employeeName,
      by: employeeName,
      shortAlias: `T${aliasNumeric}`,
      fullAlias: `Table ${aliasNumeric}`,
      alias: aliasNumeric,
      isPrint,
      name: it.name || "",
      reason,
      qty,
      amount: price * qty,
      subItemId: it.subItemId || it.subitemid || 0,
      // shift: "Shift 1",
      price,
    }
  })
}

const saveLocalStorage = (key: string, payload: any) => {
  if (!process.client) return
  try {
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}:${key}`, JSON.stringify(payload))
  } catch (error) {
    console.error("voidItem: failed to save local", error)
  }
}

const removeLocalStorage = (key: string) => {
  if (!process.client) return
  try {
    localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}:${key}`)
  } catch (error) {
    console.error("voidItem: failed to remove local", error)
  }
}

const getPending = (): { key: string; payload: any }[] => {
  if (!process.client) return []
  const out: { key: string; payload: any }[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(`${LOCAL_STORAGE_PREFIX}:`)) {
        const raw = localStorage.getItem(k)
        if (raw) out.push({ key: k.slice(LOCAL_STORAGE_PREFIX.length + 1), payload: JSON.parse(raw) })
      }
    }
  } catch (error) {
    console.error("voidItem: failed to read pending", error)
  }
  return out
}

const postOne = async (dataservice: ReturnType<typeof useDataservice>, key: string, payload: any) => {
  try {
    await dataservice.voidItems.post(payload)
    removeLocalStorage(key)
  } catch (error) {
    console.error("voidItem: post failed, kept in local storage", error)
  }
}

export const syncPendingVoidItems = async (dataservice: ReturnType<typeof useDataservice>) => {
  const pending = getPending()
  if (!pending.length) return
  const batchSize = 25
  for (let i = 0; i < pending.length; i += batchSize) {
    const batch = pending.slice(i, i + batchSize)
    await Promise.all(batch.map(({ key, payload }) => postOne(dataservice, key, payload)))
  }
}

export default defineNuxtPlugin(({ $pinia }) => {
  if (!process.client) return

  const jobV1Store = usePosJobsStore()
  const dataservice = useDataservice()
  const customerId = dataservice.customerId

  onDataserviceReady(dataservice, () => void syncPendingVoidItems(dataservice))
  window.addEventListener("online", () => {
    if (dataservice.isInited.value) void syncPendingVoidItems(dataservice)
  })

  jobV1Store.$onAction((action) => {
    if (action.name !== "add") return
    const job = action.args[0] as JobCard
    const isSync = action.args[1]
    if (!isSync) return
    if (job?.type !== JobAction.VOID && job?.type !== JobAction.VOID_TOPPING) return

    action.after(() => {
      const payloads = buildPayloads(job, customerId.value)
      payloads.forEach((p) => {
        const key = `${p.timestamp}__${p.subItemId}`
        saveLocalStorage(key, p)
        void postOne(dataservice, key, p)
      })
    })
  })
})
